import { projectSchema } from '@/models/projectSchema';
import { annotationJobSchema } from '@/models/annotatorSchema';
import { MappingFileContent } from '@/components/admin/sections/ZipConfigSection/config.types';
import {
  PROJECTS_FETCHING_ERROR_PREFIX,
  PROJECT_REMOVAL_ERROR_PREFIX,
} from '@/redux/projects/projects.constants';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { AnnotationJob as ProjectJob, PageOf, Project } from '@/types/models';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { z } from 'zod';
import { logEntrySchema } from '@/models/logEntrySchema';
import { FetchLogsParams } from '@/types/logEntry';
import { pageableSchema } from '@/models/pageableSchema';
import { apiPath } from '../apiPath';

const MIN_PAGES_FOR_ADDITIONAL_FETCH = 1;
const START_PAGE_INDEX_FOR_ADDITIONAL_FETCH = 1;
const PROJECTS_PER_PAGE = 100;
const PAGE_INCREMENT = 1;

const logsResponseSchema = pageableSchema(logEntrySchema);

export type LogsResponse = z.infer<typeof logsResponseSchema>;

export const fetchProjectLogs = createAsyncThunk<LogsResponse, FetchLogsParams, ThunkConfig>(
  'projects/fetchLogs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.get(apiPath.getProjectLogs(params));

      const isDataValid = validateDataUsingZodSchema(response.data, logsResponseSchema);
      if (!isDataValid) {
        throw new Error('Invalid logs data received from server');
      }

      return response.data;
    } catch (error) {
      let errorMessage = 'Error fetching project logs';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const getAllProjects = createAsyncThunk<Project[], void, ThunkConfig>(
  'project/getAllProjects',
  async () => {
    try {
      const firstPageResponse = await axiosInstanceNewAPI.get<PageOf<Project>>(
        apiPath.getProjects(),
        {
          params: { page: 0, size: PROJECTS_PER_PAGE },
        },
      );
      let allProjects: Project[] = [];
      if (firstPageResponse.data && Array.isArray(firstPageResponse.data.content)) {
        allProjects = [...firstPageResponse.data.content];
        if (
          typeof firstPageResponse.data.totalPages === 'number' &&
          firstPageResponse.data.totalPages > MIN_PAGES_FOR_ADDITIONAL_FETCH
        ) {
          const { totalPages } = firstPageResponse.data;

          const additionalPageRequests = [];
          for (
            let page = START_PAGE_INDEX_FOR_ADDITIONAL_FETCH;
            page < totalPages;
            page += PAGE_INCREMENT
          ) {
            additionalPageRequests.push(
              axiosInstanceNewAPI.get<PageOf<Project>>(apiPath.getProjects(), {
                params: { page, size: PROJECTS_PER_PAGE },
              }),
            );
          }

          const EMPTY_ARRAY_LENGTH = 0;
          if (additionalPageRequests.length > EMPTY_ARRAY_LENGTH) {
            const additionalPagesResponses = await Promise.all(additionalPageRequests);

            additionalPagesResponses.forEach(pageResponse => {
              if (pageResponse.data && Array.isArray(pageResponse.data.content)) {
                allProjects = [...allProjects, ...pageResponse.data.content];
              }
            });
          }
        }
      } else {
        throw new Error('Unexpected response structure received from server');
      }

      return allProjects;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECTS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export interface CreateProjectPayload {
  name: string;
  license?: string;
  customLicenseName?: string;
  customLicenseUrl?: string;
  projectId?: string;
  disease?: string;
  organism?: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat?: boolean;
  fileId: string;
}

export interface CreateProjectWithFilePayload {
  file: File;
  name: string;
  license?: string;
  customLicenseName?: string;
  customLicenseUrl?: string;
  projectId?: string;
  disease?: string;
  organism?: string;
  version?: string;
  notifyEmail?: string;
  sbgnFormat?: boolean;
}

export const createProjectWithFile = createAsyncThunk<
  Project,
  CreateProjectWithFilePayload,
  ThunkConfig
>('projects/createProjectWithFile', async (data, { rejectWithValue }) => {
  try {
    const formData = new FormData();

    formData.append('file', data.file);

    formData.append('name', data.name);
    if (data.license && data.license !== 'OTHER') formData.append('license', data.license);
    if (data.license === 'OTHER' && data.customLicenseName) {
      formData.append('customLicenseName', data.customLicenseName);
    } else if (data.license === 'OTHER') {
      formData.append('customLicenseName', '');
    }
    if (data.license === 'OTHER' && data.customLicenseUrl) {
      formData.append('customLicenseUrl', data.customLicenseUrl);
    } else if (data.license === 'OTHER') {
      formData.append('customLicenseUrl', '');
    }
    if (data.projectId) formData.append('projectId', data.projectId);
    if (data.disease) formData.append('disease', data.disease);
    if (data.organism) formData.append('organism', data.organism);
    if (data.version) formData.append('version', data.version);
    if (data.notifyEmail) formData.append('notifyEmail', data.notifyEmail);
    if (data.sbgnFormat !== undefined) formData.append('sbgnFormat', String(data.sbgnFormat));

    const response = await axiosInstanceNewAPI.post<Project>(apiPath.getProjects(), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

    if (!isDataValid) {
      throw new Error('Invalid project data received from server');
    }

    return response.data;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Error creating project with file';

    return rejectWithValue(errorMsg);
  }
});

export interface CreateProjectFromFilePayload {
  projectId: string;
  fileId: number;
  complex: boolean;
  converterType: string;
  config: MappingFileContent;
}

export const createProjectFromFile = createAsyncThunk<
  ProjectJob,
  CreateProjectFromFilePayload,
  ThunkConfig
>('projects/createProjectFromFile', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstanceNewAPI.post(apiPath.createProjectFromFile(), payload);

    const isDataValid = validateDataUsingZodSchema(response.data, annotationJobSchema);
    if (!isDataValid) {
      throw new Error('Invalid job data received from server');
    }

    return response.data;
  } catch (error) {
    let errorMessage = 'Error creating project';
    if (error instanceof Error) {
      errorMessage = error.message;
    }
    return rejectWithValue(errorMessage);
  }
});

/**
 * Re-fetches a project that is already on the list, e.g. after a websocket message told us it
 * changed. Unlike getProjectById it never adds a project to the list: a project can be removed
 * while this request is in flight, and adding it back would resurrect a project that is gone.
 */
export const refreshProjectOnList = createAsyncThunk<Project | undefined, string, ThunkConfig>(
  'projects/refreshProjectOnList',
  async projectId => {
    try {
      const response = await axiosInstanceNewAPI.get<Project>(apiPath.getProjectById(projectId));

      const isDataValid = validateDataUsingZodSchema(response.data, projectSchema);

      return isDataValid ? response.data : undefined;
    } catch (error) {
      return Promise.reject(getError({ error, prefix: PROJECTS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const removeProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/removeProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.removeProject(projectId));
      return projectId;
    } catch (error: unknown) {
      let errorMessage = PROJECT_REMOVAL_ERROR_PREFIX;

      if (error instanceof Error) {
        if (axios.isAxiosError(error)) {
          errorMessage += extractAxiosErrorMessage(error);
        } else {
          errorMessage += error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage += error;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const shareProjectInMinervaNet = createAsyncThunk<string, string, ThunkConfig>(
  'projects/shareProjectInMinervaNet',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.registerProject(projectId));
      return projectId;
    } catch (error) {
      return rejectWithValue('Failed to share project in Minerva-Net');
    }
  },
);

export const unshareProjectInMinervaNet = createAsyncThunk<string, string, ThunkConfig>(
  'projects/unshareProjectInMinervaNet',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.unregisterProject(projectId));
      return projectId;
    } catch (error) {
      return rejectWithValue('Failed to unshare project from Minerva-Net');
    }
  },
);

export const archiveProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/archiveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.archiveProject(projectId), {});

      return projectId;
    } catch (error) {
      let errorMessage = 'Failed to archive project';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const reviveProject = createAsyncThunk<string, string, ThunkConfig>(
  'projects/reviveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.post(apiPath.reviveProject(projectId), {});

      return projectId;
    } catch (error) {
      let errorMessage = 'Failed to unarchive project';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export interface GetZipFromGitPayload {
  url: string;
  branch?: string;
}

export const getZipFileFromGit = createAsyncThunk<File, GetZipFromGitPayload, ThunkConfig>(
  'projects/getZipFromGit',
  async (gitData, { rejectWithValue }) => {
    try {
      const dataToSend = { ...gitData };

      const response = await axiosInstanceNewAPI.post<Blob>(apiPath.getZipFromGit(), dataToSend, {
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      });

      const filename = `git-export-${Date.now()}.zip`;
      const file = new File([response.data], filename, { type: 'application/zip' });

      return file;
    } catch (error) {
      let errorMessage = 'Error downloading zip from git';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  },
);
